// components/pages/ProtectedRoute.jsx
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';

const ProtectedRoute = ({ children, adminOnly = false, cashierOnly = false, requiredPermission = null, superAdminOnly = false }) => {
  const { isAuthenticated, user, loading } = useSelector((s) => s.auth);
  const { slug } = useParams();
  const [clientIdOfSlug, setClientIdOfSlug] = useState(null);
  const [loadingClient, setLoadingClient] = useState(!!slug && (adminOnly || !!requiredPermission));
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!adminOnly && !requiredPermission) return;

    (async () => {
      try {
        setFetchError(false);
        setLoadingClient(true);
        const { data } = await axios.get(`/api/clients/${slug}`);
        if (active) setClientIdOfSlug(data._id);
      } catch (e) {
        console.error('Error verificando cliente:', e);
        if (active) setFetchError(true);
      } finally {
        if (active) setLoadingClient(false);
      }
    })();
    return () => { active = false; };
  }, [slug, adminOnly, requiredPermission]);

  if (loading || ((adminOnly || requiredPermission) && loadingClient)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 font-medium">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  if (fetchError && (adminOnly || requiredPermission)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-6">
        <div className="bg-surface p-8 rounded-3xl border border-white/5 text-center shadow-2xl max-w-sm">
          <p className="text-red-400 font-bold mb-4">Error de conexión</p>
          <p className="text-white/40 text-sm mb-6">No pudimos verificar tu acceso al espacio "{slug}". Por favor, intentá recargar la página.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-brand-gradient text-white rounded-xl font-bold text-sm shadow-lg shadow-orange-500/20 active:scale-95 transition-all"
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={slug ? `/${slug}` : "/"} replace />;
  }

  if (superAdminOnly) {
    const superAdminEmail = process.env.REACT_APP_SUPER_ADMIN_EMAIL;
    if (!superAdminEmail || user.email !== superAdminEmail) {
      return <Navigate to={slug ? `/${slug}` : "/"} replace />;
    }
    return children;
  }

  // Multitenancy check
  const membership = user?.memberships?.find(
    (m) => m.clientId && clientIdOfSlug && m.clientId.toString() === clientIdOfSlug.toString()
  );

  const localRole = membership?.role;
  const localPermissions = membership?.permissions || [];

  const isGlobalAdmin = user?.role === 'admin' && !user?.clientId; // Superadmin
  const isLegacyTenantAdmin = user?.role === 'admin' && user?.clientId && clientIdOfSlug && (user.clientId.toString() === clientIdOfSlug.toString());
  
  const isTenantAdmin = isLegacyTenantAdmin || localRole === 'admin';
  const isAuthorizedEmployee = (localRole === 'employee' || (user?.role === 'employee' && user?.clientId === clientIdOfSlug)) && 
                               (requiredPermission ? (localPermissions.includes(requiredPermission) || user?.permissions?.includes(requiredPermission)) : true);
  const isAuthorizedCashier = localRole === 'cashier' || (user?.role === 'cashier' && user?.clientId === clientIdOfSlug);

  if (adminOnly) {
    if (!isGlobalAdmin && !isTenantAdmin) {
      return <Navigate to={`/${slug}`} replace />;
    }
  } else if (cashierOnly) {
    if (!isGlobalAdmin && !isTenantAdmin && !isAuthorizedCashier) {
      return <Navigate to={`/${slug}`} replace />;
    }
  } else if (requiredPermission) {
    if (!isGlobalAdmin && !isTenantAdmin && !isAuthorizedEmployee) {
      return <Navigate to={`/${slug}`} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;



// import React from 'react';
// import { useSelector } from 'react-redux';
// import { Navigate } from 'react-router-dom';

// const ProtectedRoute = ({ children, adminOnly = false }) => {
//     const { isAuthenticated, user } = useSelector((state) => state.auth);

//     if (!isAuthenticated) {
//         return <Navigate to="/" />; // Redirige al inicio si no está autenticado
//     }

//     if (adminOnly && user.role !== 'admin') {
//         return <Navigate to="/" />; // Redirige al inicio si no es administrador
//     }

//     return children;
// };

// export default ProtectedRoute;
